import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { FaCopy } from 'react-icons/fa';

import Button from 'common/components/button/Button';
import Modal from 'common/components/modal/Modal';
import Select from 'common/components/select/Select';
import Label from 'common/components/label/Label';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { Data } from 'common/components/spreadsheet/types';

type Props = {
    vehicles: SelectValueLabel[];
    rows: Data<string>[];
    selectedRowIds: string[];
    setRows: (rows: Data<string>[]) => void;
};

// Task #66347: um contrato de frota é o mesmo contrato para vários ativos. Como na base de dados um
// contrato pertence sempre a um ativo, replicamos a linha: escolhe-se a linha modelo, escolhem-se os
// ativos, e a grelha passa a ter uma linha por ativo — cada uma continua editável à parte.
const ReplicateRowsButton = ({ vehicles, rows, selectedRowIds, setRows }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVehicles, setSelectedVehicles] = useState<SelectValueLabel[]>([]);

    const sourceRow = useMemo(
        () => (selectedRowIds.length === 1 ? rows.find(row => (row as any).__index === selectedRowIds[0]) : undefined),
        [rows, selectedRowIds]);

    const canReplicate = !!sourceRow;

    const open = () => {
        if (!canReplicate) {
            addToast(t('bulk_contracts.replicate.select_one_row'), { appearance: 'warning' });
            return;
        }
        setSelectedVehicles([]);
        setIsOpen(true);
    };

    const confirm = () => {
        if (!sourceRow || !selectedVehicles.length) return;

        const sourceIndex = rows.findIndex(row => row === sourceRow);
        // A linha modelo fica com o primeiro ativo e as restantes entram logo a seguir, para o
        // utilizador ver o bloco todo junto.
        const replicated = selectedVehicles.map(vehicle => {
            const { __index, __errors, ...values } = sourceRow as any;
            return { ...values, vehicleId: vehicle.value } as Data<string>;
        });

        const next = [...rows];
        next.splice(sourceIndex, 1, ...replicated);
        setRows(next);

        setIsOpen(false);
        addToast(t('bulk_contracts.replicate.done', { count: replicated.length }), { appearance: 'success' });
    };

    return (
        <>
            <Button preset={'primary'} size={'small'} onlyIcon={true}
                onClick={open}
                disabledMessage={t('bulk_contracts.replicate.button_title') as string}
            >
                <FaCopy />
            </Button>

            <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} style={{ content: { minWidth: '32rem', padding: '1.5rem' } }}>
                <h3>{t('bulk_contracts.replicate.modal_title')}</h3>
                <p>{t('bulk_contracts.replicate.modal_hint')}</p>

                <div style={{ marginTop: '1rem' }}>
                    <Label>{t('bulk_contracts.columns.vehicle')}</Label>
                    <Select
                        isMulti
                        options={vehicles}
                        value={selectedVehicles}
                        placeholder={t('bulk_contracts.replicate.vehicles_placeholder')}
                        onChange={(values: any) => setSelectedVehicles((values ?? []) as SelectValueLabel[])}
                    />
                </div>

                <div style={{ textAlign: 'right', marginTop: '1.5rem' }}>
                    <Button preset={'secondary'} onClick={() => setIsOpen(false)} text={t('common.cancel') as string} />
                    <Button onClick={confirm} isDisabled={!selectedVehicles.length}
                        text={t('bulk_contracts.replicate.confirm', { count: selectedVehicles.length }) as string} />
                </div>
            </Modal>
        </>
    );
};

export default ReplicateRowsButton;
